/**
 * ConfirmModal — reusable confirmation dialog.
 * Replaces all window.confirm() calls across the app.
 *
 * Usage:
 *   const [confirm, setConfirm] = useState(null);
 *
 *   // To trigger:
 *   setConfirm({
 *     title: 'Delete voice?',
 *     message: 'This cannot be undone.',
 *     confirmLabel: 'Delete',          // optional, default 'Confirm'
 *     destructive: true,               // optional, makes button red
 *     onConfirm: () => handleDelete(), // called on confirm
 *   });
 *
 *   // In JSX:
 *   {confirm && <ConfirmModal {...confirm} onClose={() => setConfirm(null)} />}
 */
import React from 'react';

export default function ConfirmModal({
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel  = 'Cancel',
  destructive  = false,
  onConfirm,
  onClose,
}) {
  const handleConfirm = () => {
    onClose();
    onConfirm?.();
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(10,26,24,0.55)',
        zIndex: 9999,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        animation: 'cfmFadeIn 0.15s ease',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <style>{`
        @keyframes cfmFadeIn  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes cfmSlideUp { from { transform: translateY(24px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
      `}</style>

      <div style={{
        background: '#F5EFE8',
        borderRadius: '24px 24px 0 0',
        padding: '28px 24px 40px',
        width: '100%',
        maxWidth: '480px',
        animation: 'cfmSlideUp 0.2s ease',
      }}>
        {/* Handle bar */}
        <div style={{ width: '36px', height: '4px', background: 'rgba(30,107,104,0.15)', borderRadius: '2px', margin: '0 auto 24px' }} />

        <h3 style={{
          margin: '0 0 10px',
          fontFamily: "'Playfair Display', serif",
          fontSize: '1.2rem', fontWeight: '500', color: '#1E3A39',
        }}>
          {title}
        </h3>

        {message && (
          <p style={{ margin: '0 0 28px', fontSize: '0.88rem', color: '#8FA3A0', lineHeight: 1.6 }}>
            {message}
          </p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <button
            onClick={handleConfirm}
            style={{
              padding: '14px',
              background: destructive
                ? 'linear-gradient(135deg, #C0392B, #E74C3C)'
                : 'linear-gradient(135deg, #1E6B68, #2D8B87)',
              color: '#fff',
              border: 'none',
              borderRadius: '14px',
              fontSize: '0.95rem',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: destructive
                ? '0 4px 14px rgba(192,57,43,0.25)'
                : '0 4px 14px rgba(30,107,104,0.25)',
            }}
          >
            {confirmLabel}
          </button>

          <button
            onClick={onClose}
            style={{
              padding: '14px',
              background: 'transparent',
              color: '#8FA3A0',
              border: '1.5px solid rgba(184,211,207,0.5)',
              borderRadius: '14px',
              fontSize: '0.92rem',
              cursor: 'pointer',
            }}
          >
            {cancelLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
